import {
  Badge,
  Button,
  Center,
  Container,
  Heading,
  Spacer,
  Stack,
  Text,
  useColorMode,
  VStack,
} from '@chakra-ui/react';
import { CheckCircledIcon } from '@radix-ui/react-icons';
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchInvitations } from '../api/Invitations';
import { frontendUrl } from '../config';
import { routes } from '../data/routes';
import ShortUuids from '../lib/ShortUuids';
import wrapColorTheme from '../lib/WrapColorTheme';
import { colors, styled } from '../Stitches';
import { Authenticated } from './AuthGuards';
import { CopyToClipboard } from './CopyToClipboard';
import Emoji from './Emoji';
import { Fade } from './Fade';
import { Fetching } from './Fetching';
import { useAuthenticateApiCall } from './hooks/useAuthenticateApiCall';
import { PageHeader } from './PageHeader';

export type Invitation = {
  id: string;
  redeemed: boolean;
  userId: string;
};

export type LetterInvitation = Invitation & {
  title: string;
  tempContactName?: string;
  recipientName?: string;
};

const HoverScale = styled('div', {
  transition: 'transform 0.1s ease',
  backfaceVisibility: 'hidden',

  '&:hover': {
    transform: 'scale(1.01)',
  },
});

const InviteLinkPrimitive = styled('code', {
  fontFamily: '$mono',

  padding: '$4',
  fontSize: '$1',
  borderRadius: '$2',
  textAlign: 'center',

  variants: {
    redeemed: {
      true: { textDecoration: 'line-through' },
    },

    theme: {
      dark: { backgroundColor: colors.dark.whiteAlpha100 },
      light: { backgroundColor: colors.gray100 },
    },
  },
});

const InviteLink = wrapColorTheme(InviteLinkPrimitive);

type InvitationItemProps = { id: string; redeemed: boolean };

const useShortInvitationLink = (id: string) => {
  const shortId = ShortUuids.encode(id);
  return `${frontendUrl}/write-to-me/${shortId}`;
};

const InvitationItem = ({ id, redeemed }: InvitationItemProps) => {
  const link = useShortInvitationLink(id);

  return (
    <HoverScale>
      <Stack
        direction={['column', null, 'row']}
        p={3}
        spacing={4}
        rounded="base"
        border="1px"
        borderColor="gray.200"
        justifyContent="space-between"
        alignItems="center"
        width={['xs', 'md', 'container.md']}
        boxShadow="base"
      >
        <InviteLink redeemed={redeemed}>{link}</InviteLink>

        {redeemed ? (
          <Badge w="3xs" p={2} rounded="base" variant="solid" colorScheme="purple">
            <Stack direction="row" align="center">
              <CheckCircledIcon /> <Text>Redeemed</Text>
            </Stack>
          </Badge>
        ) : (
          <CopyToClipboard text={link} copiedText="Invite link copied" />
        )}
      </Stack>
    </HoverScale>
  );
};

const Boxed = styled('div', {
  padding: '$4',
  px: '$5',
  borderRadius: '$3',
  borderWidth: '$1',
  boderColor: 'gray.200',
});

type InvitationListProps = { data: Invitation[] | undefined; error: any };

const InvitationList = ({ data, error }: InvitationListProps) => {
  const numberOfInvitationsRedeemed = data?.filter((i) => i.redeemed).length;

  if (error) {
    return <Boxed>Something went wrong.</Boxed>;
  } else if (data === undefined || (data && data.length === 0)) {
    return (
      <Boxed>
        <Text>No Active Invitations</Text>
      </Boxed>
    );
  } else {
    return (
      <>
        <div>
          <Heading size="lg" fontWeight={'medium'}>
            Invite Links
          </Heading>
          <Spacer h={2} />
          <Text>
            <strong>
              Invitations Redeemed: ({numberOfInvitationsRedeemed}/{data ? data.length : 5})
            </strong>
          </Text>
        </div>
        <Spacer h={2} />
        {data.map(({ id, redeemed }: Invitation) => (
          <InvitationItem key={id} id={id} redeemed={redeemed} />
        ))}
      </>
    );
  }
};

const Inlines = styled('div', {
  '> *': {
    display: 'inline',
    marginRight: 8,
  },
});

const useShortLetterLink = (id: string) => {
  const shortId = ShortUuids.encode(id);
  return `${frontendUrl}/letter-invite/${shortId}`;
};

const InvitationalLetterLine = ({ invite }: { invite: LetterInvitation }) => {
  const { colorMode } = useColorMode();
  const link = useShortLetterLink(invite.id);

  return (
    <HoverScale>
      <Stack
        p={4}
        rounded="base"
        border="1px"
        borderColor="gray.200"
        width={['xs', 'md', 'container.md']}
        boxShadow="base"
      >
        <Inlines>
          <Text>
            <strong>Recipient:</strong> {invite.tempContactName || invite.recipientName}
          </Text>
          <Text>
            <strong>Title:</strong> {invite.title}
          </Text>
          <Text>
            <strong>Status:</strong> {invite.redeemed ? 'Invite Accepted' : 'Pending'}
          </Text>
        </Inlines>
        <Stack
          direction={['column', null, 'row']}
          alignItems="center"
          justifyContent="space-between"
        >
          <InviteLink
            theme={colorMode}
            redeemed={invite.redeemed}
            data-test-id="letter-invite-link"
          >
            {link}
          </InviteLink>

          {invite.redeemed ? (
            <Badge w="3xs" p={2} rounded="base" variant="solid" colorScheme="purple">
              <Stack direction="row" align="center">
                <CheckCircledIcon /> <Text>Redeemed</Text>
              </Stack>
            </Badge>
          ) : (
            <CopyToClipboard text={link} copiedText="Letter link copied" />
          )}
        </Stack>
      </Stack>
    </HoverScale>
  );
};

type InvitationalLettersListProps = { letterInvites: LetterInvitation[] | undefined; error: any };
const InvitationalLettersList = ({ letterInvites, error }: InvitationalLettersListProps) => (
  <>
    <div>
      <Heading size="lg" fontWeight={'medium'}>
        Invitational Letters:
      </Heading>
      <Spacer h={2} />
      <Text maxW={'2xl'}>
        <strong>Share</strong> the letter with your friend by <em>sending them a link</em>. They
        will be able to log in or register to continue your correspondence.
      </Text>
    </div>
    <Spacer h={2} />

    {letterInvites &&
      letterInvites.map((invite) => <InvitationalLetterLine key={invite.id} invite={invite} />)}
  </>
);

const Super = styled('span', {
  position: 'relative',
  top: '-$6',

  fontFamily: '$mono',
});

const InviteWithALetter = () => (
  <div>
    <Link to={routes.inviteWithLetter}>
      <Button colorScheme={'purple'}>
        <Emoji symbol="✍️" label="Emoji of a hand writing on paper pen." spaceRight />
        Invite with a letter{' '}
      </Button>
      <Super>
        <Emoji
          symbol="✨"
          label="Emoji of a sparkle indicating that the invite with a letter button is new"
        />
        Recommended
      </Super>
    </Link>
    <Super></Super>
  </div>
);

export const Invitations = () => {
  const authedFetchInvitations = useAuthenticateApiCall(fetchInvitations);
  const { data, isLoading, error } = useQuery(['invitations'], authedFetchInvitations);

  return (
    <Container w={['xs', 'md', 'lg']} pb={12}>
      <Center>
        <VStack spacing={8} align="flex-start">
          <PageHeader
            title="Invitations"
            subtitle={'Friends are currently added through invitation letters or invitation links.'}
            emoji={{ symbol: '📮', label: 'letter box emoji' }}
          />
          <InviteWithALetter />
          <Fetching isFetching={isLoading}>
            <Stack direction="column" align="flex-start" spacing={3}>
              <InvitationalLettersList letterInvites={data?.letter} error={error} />
            </Stack>
          </Fetching>
          <Fetching isFetching={isLoading}>
            <Stack direction="column" align="flex-start" spacing={3}>
              <InvitationList data={data?.standard} error={error} />
            </Stack>
          </Fetching>
        </VStack>
      </Center>
    </Container>
  );
};

export const AuthenticatedInvitations = () => (
  <Authenticated>
    <Fade>
      <Invitations />
    </Fade>
  </Authenticated>
);
